import React, { useCallback } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { Blockchain } from '@repo/prsn-blockchain';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';

import { useBridgeManager } from '@web/features/staking/BridgeManager';
import { StakeModal } from '@web/features/staking/modals/StakeModal';
import { Button } from '@web/ui/components';
import { StakeIcon } from '@web/ui/icons';

const UnstakedDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.spacing('1000')};
`;

const StakingText = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.onSurface};
  opacity: ${({ theme }) => theme.opacity.dense};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};

  height: ${({ theme }) => theme.size['2500']};
`;

const ProcessingText = styled(StakingText)`
  justify-content: center;
`;

const PersonaShort = styled.p<{ disabled: boolean }>`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0750']};

  background: ${({ theme, disabled }) =>
    disabled && disabled ? theme.color.background : 'none'};
  opacity: ${({ theme, disabled }) =>
    disabled ? theme.opacity.solid : theme.opacity.opaque};
`;

const ActionContainer = styled.div``;

interface IUnstakedGridItemDetailsProps {
  persona: IPersonaFront;
  processing: boolean;
  locked: boolean;
}

export const UnstakedGridItemDetails = ({
  persona,
  locked,
  processing,
}: IUnstakedGridItemDetailsProps) => {
  const t = useTranslations();

  const { createInstance } = useBridgeManager();

  const {
    value: isStakeModalOpen,
    setTrue: openStakeModal,
    setFalse: closeStakeModal,
  } = useBoolean(false);

  const handleStakeClick = useCallback(() => {
    if (persona.blockchain === Blockchain.BASE) {
      openStakeModal();
    } else {
      createInstance({
        tokenIds: [persona.tokenId],
        source: Blockchain.ETHEREUM,
        stake: true,
      });
    }
  }, [persona, openStakeModal, createInstance]);

  return (
    <>
      <UnstakedDetailsContainer>
        <PersonaShort disabled={locked || processing}>
          {t('PERSONA_SHORT')} #{persona.tokenId.toString()}
        </PersonaShort>
        <ActionContainer>
          {processing ? (
            <ProcessingText>
              {t('STAKING_GRID_ITEM_STAKING_PROCESSING_TEXT')}
            </ProcessingText>
          ) : (
            <StakingText className="hover-hide">
              {t('STAKING_GRID_ITEM_NOT_STAKING_YET')}
            </StakingText>
          )}

          {!processing && !locked && (
            <Button
              className="hover-show"
              onClick={handleStakeClick}
              trailingIcon={<StakeIcon color="onYellow" />}
              label={t('BULK_ACTION_TOOLTIP_STAKE')}
              fullWidth
            />
          )}
        </ActionContainer>
      </UnstakedDetailsContainer>

      <StakeModal
        personas={[persona]}
        open={isStakeModalOpen}
        onClose={closeStakeModal}
      />
    </>
  );
};
